import type { ApiEndpoint, ConceptPage } from "../types";

const result = {
  id: "cls_01JY7P9Z6M6B3B1NQX5H2K8R4T",
  status: "review_required",
  input: { detectedLanguage: "en", outputLanguage: "en", inputType: "establishment_activity" },
  interpretation: {
    primaryActivity: "Insurance agency and brokerage services",
    ambiguity: "Crosswalk compatibility requires review"
  },
  classifications: {
    sic: [{ code: "6411", title: "Insurance Agents, Brokers, and Service", confidence: 0.97 }],
    naics: [{ code: "524210", title: "Insurance Agencies and Brokerages", confidence: 0.97 }]
  },
  relationship: {
    status: "compatible",
    method: "independent_classification_plus_crosswalk_check",
    cardinality: "one_sic_to_many_naics",
    mappingType: "derived_via_naics_2017",
    reviewRequired: true
  },
  confidence: { score: 0.97, band: "medium", decision: "human_review" },
  review: { required: true, reason: "CANDIDATE_CROSSWALK_UNVERIFIED" },
  versions: {
    classifier: "classifier_deterministic_0.1.0",
    taxonomies: { sic: "US_SIC_1987", naics: "US_NAICS_2022" }
  }
};

export const sicClassificationGuide: ConceptPage = {
  id: "sic-naics-classification",
  title: "SIC & NAICS Classification",
  group: "Classification Engine",
  description: `
    <h2>SIC &amp; NAICS Classification API</h2>
    <p>Classify an establishment's business activity independently against <strong>U.S. SIC 1987</strong> and <strong>U.S. NAICS 2022</strong>. The crosswalk is compatibility evidence only, never a one-to-one conversion.</p>

    <div class="glass-card" style="margin: 20px 0;">
      <div class="glass-card-title">Quick start</div>
      <pre><code>curl -X POST "https://api.facio.io/v1/classifications" \\
  -H "Content-Type: application/json" \\
  -H "x-api-key: facio_dev_key_..." \\
  -H "x-idempotency-key: cls-account-123-001" \\
  -d '{
    "text": "Insurance agency selling and servicing commercial policies.",
    "jurisdiction": "US",
    "taxonomies": ["US_SIC_1987", "US_NAICS_2022"],
    "mode": "accurate",
    "maxResults": 3
  }'</code></pre>
    </div>

    <h3>Authentication and headers</h3>
    <table class="parameter-table">
      <thead><tr><th>Header</th><th>Required</th><th>Purpose</th></tr></thead>
      <tbody>
        <tr><td><code>x-api-key</code></td><td>Yes</td><td>Facio developer key. Never expose it in browser-side code.</td></tr>
        <tr><td><code>Content-Type: application/json</code></td><td>For JSON bodies</td><td>Request encoding.</td></tr>
        <tr><td><code>x-idempotency-key</code></td><td>POST classification</td><td>At least 8 characters; reuse only for a safe retry of the same request.</td></tr>
        <tr><td><code>x-correlation-id</code></td><td>No</td><td>Caller-supplied UUID for trace correlation.</td></tr>
        <tr><td><code>x-tenant-id</code></td><td>No</td><td>Dedicated tenant context when provisioned.</td></tr>
      </tbody>
    </table>

    <h3>Classification semantics</h3>
    <ul>
      <li>All taxonomy codes are JSON strings. SIC leading zeroes are preserved.</li>
      <li>SIC and NAICS are classified independently from server-supplied candidates.</li>
      <li><code>classified</code>, <code>review_required</code>, <code>needs_context</code>, and <code>unsupported</code> are valid HTTP 200 outcomes.</li>
      <li>Occupation-only or insufficient descriptions return <code>needs_context</code> with follow-up questions.</li>
      <li>Unverified crosswalk evidence is marked for review and never represented as an official mapping.</li>
    </ul>

    <h3>Recommended request</h3>
    <p><code>text</code> is essential. Accuracy improves when you describe the establishment's primary activity, products or services, customers, business model, and revenue mix—not a person's occupation.</p>
    <pre><code>{
  "text": "Insurance agency selling and servicing commercial policies.",
  "jurisdiction": "US",
  "language": "auto",
  "taxonomies": ["US_SIC_1987", "US_NAICS_2022"],
  "mode": "accurate",
  "maxResults": 3,
  "context": {
    "businessModel": "commission-based agency",
    "productsServices": ["commercial insurance placement", "policy servicing"],
    "customerType": "businesses",
    "establishmentType": "insurance agency",
    "primaryActivity": "insurance agency and brokerage services",
    "revenueMix": [
      { "activity": "insurance agency and brokerage services", "percentage": 100 }
    ]
  },
  "options": {
    "includeFullDescriptions": true,
    "includeAlternatives": true,
    "includeMatchedEvidence": true
  },
  "metadata": { "externalReference": "account_123" }
}</code></pre>

    <h3>TypeScript example</h3>
    <pre><code>type ClassificationStatus =
  | "classified"
  | "review_required"
  | "needs_context"
  | "unsupported";

interface ClassificationResult {
  id: string;
  status: ClassificationStatus;
  classifications: {
    sic: Array&lt;{ code: string; title: string; confidence: number }&gt;;
    naics: Array&lt;{ code: string; title: string; confidence: number }&gt;;
  };
  review: { required: boolean; reason?: string };
}

const response: Response = await fetch(
  "https://api.facio.io/v1/classifications",
  {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "x-api-key": process.env.FACIO_API_KEY ?? "",
      "x-idempotency-key": crypto.randomUUID()
    },
    body: JSON.stringify({
      text: "Insurance agency selling and servicing commercial policies.",
      jurisdiction: "US",
      taxonomies: ["US_SIC_1987", "US_NAICS_2022"]
    })
  }
);

const classification = (await response.json()) as ClassificationResult;
if (!response.ok) throw new Error("Facio API request failed");</code></pre>

    <h3>Errors, retries, and privacy</h3>
    <p>Use exponential backoff for <code>429</code> and transient <code>503</code> responses. A <code>409</code> means the same idempotency key was reused with a different payload. Do not log raw business descriptions by default; retain IDs, versions, status, and correlation IDs for audit.</p>
    <p><strong>Scope:</strong> codes describe establishment activity. They are not occupation codes, underwriting eligibility decisions, or coverage recommendations.</p>
  `
};

const errors = [
  { status: 400, code: "INVALID_REQUEST", description: "Malformed JSON or invalid field value." },
  { status: 401, code: "UNAUTHORIZED", description: "Missing or invalid API key." },
  { status: 403, code: "FORBIDDEN", description: "The key is not authorized for this resource." },
  { status: 409, code: "IDEMPOTENCY_CONFLICT", description: "The key was reused with a different payload." },
  { status: 429, code: "RATE_LIMITED", description: "Rate limit exceeded; retry with backoff." },
  { status: 503, code: "SERVICE_UNAVAILABLE", description: "Temporary dependency or service failure." }
];

export const sicClassificationEndpoints: ApiEndpoint[] = [
  {
    id: "create-classification",
    path: "/v1/classifications",
    method: "POST",
    tag: "SIC & NAICS Classification",
    summary: "Classify a business establishment",
    description: "Classifies establishment activity independently against SIC 1987 and NAICS 2022, then reports crosswalk compatibility evidence.",
    parameters: [
      { name: "x-idempotency-key", in: "header", type: "string", required: true, description: "At least 8 characters; stable across retries." },
      { name: "x-correlation-id", in: "header", type: "UUID", required: false, description: "Optional caller trace ID." }
    ],
    requestBody: {
      description: "Business activity and optional structured context.",
      fields: {
        text: "Insurance agency selling and servicing commercial policies.",
        jurisdiction: "US",
        language: "auto",
        taxonomies: ["US_SIC_1987", "US_NAICS_2022"],
        mode: "accurate",
        maxResults: 3,
        context: { primaryActivity: "insurance agency and brokerage services", customerType: "businesses" },
        options: { includeAlternatives: true, includeMatchedEvidence: true },
        metadata: { externalReference: "account_123" }
      }
    },
    response: { status: 200, description: "Semantic classification outcome.", body: result },
    errorCodes: errors,
    extensions: { "x-facio-domain": "CLASSIFICATION", "x-facio-lifecycle-stage": "DISCOVER" }
  },
  {
    id: "get-classification",
    path: "/v1/classifications/{id}",
    method: "GET",
    tag: "SIC & NAICS Classification",
    summary: "Retrieve a classification",
    description: "Returns the stored result, taxonomy versions, confidence, and review state.",
    parameters: [{ name: "id", in: "path", type: "string", required: true, description: "Classification ID." }],
    requestBody: null,
    response: { status: 200, description: "Classification result.", body: result },
    errorCodes: errors
  },
  {
    id: "submit-classification-feedback",
    path: "/v1/classifications/{id}/feedback",
    method: "POST",
    tag: "SIC & NAICS Classification",
    summary: "Submit reviewer feedback",
    description: "Selected codes must be candidates supplied by the server for this classification.",
    parameters: [
      { name: "id", in: "path", type: "string", required: true, description: "Classification ID." },
      { name: "x-idempotency-key", in: "header", type: "string", required: true, description: "Stable retry key." }
    ],
    requestBody: {
      fields: {
        outcome: "confirmed",
        selected: [
          { taxonomy: "US_SIC_1987", code: "6411" },
          { taxonomy: "US_NAICS_2022", code: "524210" }
        ],
        source: "review_ui"
      }
    },
    response: { status: 200, description: "Feedback recorded.", body: { classificationId: result.id, accepted: true, outcome: "confirmed" } },
    errorCodes: errors
  },
  {
    id: "get-sic-code",
    path: "/v1/sic/{code}",
    method: "GET",
    tag: "SIC & NAICS Classification",
    summary: "Look up a SIC 1987 code",
    description: "Returns the SIC record and hierarchy. Pass and store codes as strings to preserve leading zeroes.",
    parameters: [{ name: "code", in: "path", type: "string", required: true, description: "SIC code, including leading zeroes." }],
    requestBody: null,
    response: { status: 200, body: { taxonomy: "US_SIC_1987", code: "6411", title: "Insurance Agents, Brokers, and Service", version: "1987" } },
    errorCodes: errors
  },
  {
    id: "get-naics-code",
    path: "/v1/naics/{code}",
    method: "GET",
    tag: "SIC & NAICS Classification",
    summary: "Look up a NAICS 2022 code",
    description: "Returns the NAICS record and hierarchy.",
    parameters: [{ name: "code", in: "path", type: "string", required: true, description: "NAICS code as a string." }],
    requestBody: null,
    response: { status: 200, body: { taxonomy: "US_NAICS_2022", code: "524210", title: "Insurance Agencies and Brokerages", version: "2022" } },
    errorCodes: errors
  },
  {
    id: "search-taxonomy",
    path: "/v1/search",
    method: "GET",
    tag: "SIC & NAICS Classification",
    summary: "Search taxonomy records",
    description: "Searches a selected taxonomy by code, title, description, and indexed terms.",
    parameters: [
      { name: "taxonomy", in: "query", type: "string", required: true, description: "US_SIC_1987 or US_NAICS_2022." },
      { name: "q", in: "query", type: "string", required: true, description: "Search text, 2–500 characters." },
      { name: "language", in: "query", type: "string", required: false, description: "Preferred result language." },
      { name: "limit", in: "query", type: "integer", required: false, description: "1–50; defaults to 10." }
    ],
    requestBody: null,
    response: {
      status: 200,
      body: { taxonomy: "US_SIC_1987", query: "insurance agency", results: [{ code: "6411", title: "Insurance Agents, Brokers, and Service", score: 0.98 }] }
    },
    errorCodes: errors
  }
];
